import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { fromNetwork } from '@adornis/baseql/symbols.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { obs } from '@adornis/chemistry/directives/observable.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { buildFullOptions } from '@adornis/data-view/client/data-view-options.js';
import { DataTable } from '@adornis/data-view/client/table/data-table.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { stateful, useEffect, useMemo, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { html, nothing } from 'lit';
import { BehaviorSubject, combineLatest, map, tap } from 'rxjs';
import * as m from '../../../../localization/out/messages.js';
import { FreeFormMailRecipient, resolveFreeFormMailRecipients } from '../../../api/admin-mail.js';
import { countAllUsers, getUserCreatedYears, subscribeAllUsers } from '../../../api/admin-users.js';
import { MatchingWeekRole } from '../../../db/enums/roles.js';
import { JobApplicationStatus } from '../../../db/job-application.js';
import { formatAccountDate } from '../../../shared/account-status.js';
import { FREE_FORM_MAIL_MAX_RECIPIENTS } from '../../../shared/free-form-mail.js';
import { openMailComposeDialog } from '../../components/mail-compose-dialog.js';
import {
  createSelectionStore,
  isEmptySelection,
  isIDSelected,
  selectionCount,
} from '../../components/selection-state.js';
import { adminRouter } from './admin-routes.js';

type InterviewOutcomeFilter = '' | 'ATTENDED' | 'NOT_ATTENDED' | 'PENDING';

type RoleFilterValue = 'ALL' | 'ADMIN' | 'COMPANY' | 'CANDIDATE';

const renderRoleBadges = (entity: User) => {
  const names = new Set((entity.roles ?? []).map(r => r.name));
  const badges: Array<{ label: string; cls: string }> = [];
  if (names.has(MatchingWeekRole.ADMIN))
    badges.push({ label: m.admin_user_list_role_badge_admin(), cls: 'badge-secondary' });
  if (names.has(MatchingWeekRole.COMPANY))
    badges.push({ label: m.admin_user_list_role_badge_company(), cls: 'badge-primary' });
  if (badges.length === 0) badges.push({ label: m.admin_user_list_role_badge_candidate(), cls: 'badge-ghost' });
  return html`<div class="flex flex-wrap gap-1">
    ${badges.map(b => html`<span class="badge badge-sm ${b.cls}">${b.label}</span>`)}
  </div>`;
};

/**
 * Build an AdornisFilter for the current search + role selection.
 *
 *   - Search:   regex on email / firstName / lastName via `search` + `searchFields`.
 *   - Role:     a `StringAspect(equals)` on `roles.name`; mongo auto-matches
 *               any element of the nested `roles[]` array.
 */
const buildFilter = (search: string, role: RoleFilterValue) => {
  const aspects: StringAspect[] = [];
  if (role === 'ADMIN') {
    aspects.push(new StringAspect({ fieldName: 'roles.name', value: MatchingWeekRole.ADMIN, mode: 'equals' }));
  } else if (role === 'COMPANY') {
    aspects.push(new StringAspect({ fieldName: 'roles.name', value: MatchingWeekRole.COMPANY, mode: 'equals' }));
  } else if (role === 'CANDIDATE') {
    // Candidate = a user holding neither MW role; `containsNONE` matches docs where
    // no `roles.name` array element equals admin or company.
    aspects.push(
      new StringAspect({
        fieldName: 'roles.name',
        value: `${MatchingWeekRole.ADMIN},${MatchingWeekRole.COMPANY}`,
        mode: 'containsNONE',
      } as any),
    );
  }
  return new AdornisFilter({
    search: search.trim() || undefined,
    searchFields: ['email', 'firstName', 'lastName'],
    aspects,
  });
};

export const AdminUserList = stateful(() => {
  const [search, setSearch] = useState('');
  const [role, setRole] = useState<RoleFilterValue>('ALL');
  const [applicationStatus, setApplicationStatus] = useState<string>('');
  const [interviewOutcome, setInterviewOutcome] = useState<InterviewOutcomeFilter>('');
  const [createdYear, setCreatedYear] = useState<number | undefined>(undefined);
  const [activeOnly, setActiveOnly] = useState(false);
  const createdYearsQuery = useQuery(() => getUserCreatedYears()(), []);

  const [selectionStore] = useState(createSelectionStore(), []);
  useEffect(() => selectionStore.clear(), [search, role, applicationStatus, interviewOutcome, createdYear, activeOnly]);
  const totalQuery = useQuery(
    () =>
      countAllUsers(
        buildFilter(search, role),
        applicationStatus || undefined,
        interviewOutcome || undefined,
        createdYear,
        activeOnly,
      )(),
    [search, role, applicationStatus, interviewOutcome, createdYear, activeOnly],
  );
  const [mailPending, setMailPending] = useState(false);
  const [propsRef] = useState({ ids: [] as string[], mailPending: false }, []);
  propsRef.mailPending = mailPending;
  const [pageIDs] = useState(new BehaviorSubject<string[]>([]), []);

  const composeMail = async (selectedIDs?: string[]) => {
    if (propsRef.mailPending) return;
    propsRef.mailPending = true;
    setMailPending(true);
    try {
      if (selectedIDs && selectedIDs.length > FREE_FORM_MAIL_MAX_RECIPIENTS) {
        void XSnackbar.show(m.admin_user_list_mail_selection_too_many());
        return;
      }
      const recipients = await resolveFreeFormMailRecipients(
        selectedIDs ?? null,
        selectedIDs ? null : buildFilter(search, role),
        applicationStatus || undefined,
        interviewOutcome || undefined,
        createdYear,
        activeOnly,
      )(selectionSet(() => FreeFormMailRecipient, 1));
      if (!recipients.length) {
        void XSnackbar.show(
          selectedIDs ? m.admin_user_list_mail_selection_empty() : m.admin_user_list_mail_filter_empty(),
        );
        return;
      }
      if (recipients.length > FREE_FORM_MAIL_MAX_RECIPIENTS) {
        void XSnackbar.show(
          selectedIDs ? m.admin_user_list_mail_selection_too_many() : m.admin_user_list_mail_filter_too_many(),
        );
        return;
      }
      if (await openMailComposeDialog(recipients)) selectionStore.clear();
    } catch (error) {
      void XSnackbar.show(error instanceof Error ? error.message : m.admin_user_list_mail_error());
    } finally {
      propsRef.mailPending = false;
      setMailPending(false);
    }
  };

  const userDataViewOptions = useMemo(() => {
    const pages = new Map<number, string[]>();
    propsRef.ids = [];
    pageIDs.next([]);
    return [
      buildFullOptions({
        class: User,
        // Wrap the registered subscription so the additional filters ride along as extra
        // args; the data-view only ever passes (skip, limit, filter) itself.
        operation: (skip: number, limit: number, filter: AdornisFilter) => gqlFields => {
          // A new pagination session starts at zero, including after sorting.
          if (skip === 0) {
            pages.clear();
            propsRef.ids = [];
            pageIDs.next([]);
          }
          return subscribeAllUsers(
            skip,
            limit,
            filter,
            applicationStatus || undefined,
            interviewOutcome || undefined,
            createdYear,
            activeOnly,
          )(gqlFields).pipe(
            tap(users => {
              pages.set(
                skip,
                users.map(user => user._id!),
              );
              propsRef.ids = [...new Set([...pages.entries()].sort(([a], [b]) => a - b).flatMap(([, ids]) => ids))];
              pageIDs.next(propsRef.ids);
            }),
            map(users =>
              users.map(user => {
                // Preserve remote values: local construction would default a missing createdAt to now.
                const data = { ...user.toObject(), [fromNetwork]: true };
                return new User(data);
              }),
            ),
          );
        },
        customColumns: [
          {
            customID: '_select',
            columnName: obs(
              combineLatest([selectionStore.observable, pageIDs]).pipe(
                map(([selected]) => {
                  const ids = propsRef.ids;
                  const checked = ids.length > 0 && ids.every(id => selected.includes(id));
                  const indeterminate = !checked && ids.some(id => selected.includes(id));
                  return html`
                    <div class="flex items-center justify-center h-full">
                      <input
                        type="checkbox"
                        class="checkbox checkbox-sm"
                        aria-label=${m.admin_user_list_mail_select_page()}
                        .checked=${checked}
                        .indeterminate=${indeterminate}
                        @click=${(event: Event) => event.stopPropagation()}
                        @change=${(event: Event) => selectionStore.toggleSubset(propsRef.ids, (event.currentTarget as HTMLInputElement).checked)}
                      />
                    </div>
                  `;
                }),
              ),
            ) as any,
            columnWidth: '3rem',
            isSortable: false,
            selectionSet: { _id: 1 },
            renderItem: (user: User) =>
              obs(
                selectionStore.observable.pipe(
                  map(
                    selected => html`
                      <input
                        type="checkbox"
                        class="checkbox checkbox-sm"
                        aria-label=${m.admin_user_list_mail_select_user()}
                        .checked=${isIDSelected(selected, user._id!)}
                        @click=${(event: Event) => event.stopPropagation()}
                        @change=${(event: Event) => selectionStore.toggle(user._id!, (event.currentTarget as HTMLInputElement).checked)}
                      />
                    `,
                  ),
                ),
              ),
          },
          { columnName: m.admin_user_list_column_email(), fieldName: 'email' },
          { columnName: m.admin_user_list_column_first_name(), fieldName: 'firstName' },
          { columnName: m.admin_user_list_column_last_name(), fieldName: 'lastName' },
          {
            columnName: m.admin_user_list_column_created_at(),
            fieldName: 'createdAt',
            isSortable: true,
            renderItem: entity => formatAccountDate(entity.has('createdAt') ? entity.createdAt : null),
          },
          {
            columnName: m.admin_user_list_column_last_active_at(),
            fieldName: 'lastActiveAt',
            isSortable: true,
            renderItem: entity => formatAccountDate(entity.has('lastActiveAt') ? entity.lastActiveAt : null),
          },
          {
            // Custom-rendered column with no scalar fieldName — needs an explicit
            // customID so the data-view doesn't reject it.
            customID: 'roles',
            columnName: m.admin_user_list_column_roles(),
            selectionSet: { roles: { _id: 1, name: 1 } } as any,
            renderItem: entity => renderRoleBadges(entity as User),
          },
        ],
        isEntryClickable: true,
        entryClickHandler: entity => {
          goTo(adminRouter.link('userDetail', { params: { userID: entity._id! } }));
        },
        isSortable: true,
        view: new View({ filter: buildFilter(search, role) }),
      }),
    ];
  }, [search, role, applicationStatus, interviewOutcome, createdYear, activeOnly]);

  return html`
    <div class="p-8">
      <div class="flex justify-between items-center mb-6 gap-4 flex-wrap">
        <h1 class="text-3xl font-bold text-base-content">${m.admin_user_list_title()}</h1>
        <button class="btn btn-primary" @click=${() => adminRouter.goTo('userCreate')}>
          <span class="icon-[material-symbols--person-add]"></span>
          ${m.create_user()}
        </button>
      </div>

      <div class="flex flex-wrap gap-3 items-end mb-4">
        <label class="form-control flex-1 min-w-[240px]">
          <div class="label py-1"><span class="label-text">${m.admin_user_list_search_label()}</span></div>
          <input
            type="search"
            class="input input-bordered w-full"
            placeholder=${m.admin_user_list_search_placeholder()}
            .value=${search}
            @input=${(e: InputEvent) => setSearch((e.target as HTMLInputElement).value)}
          />
        </label>
        <label class="form-control min-w-[180px]">
          <div class="label py-1"><span class="label-text">${m.admin_user_list_role_filter_label()}</span></div>
          <select
            class="select select-bordered"
            .value=${role}
            @change=${(e: Event) => setRole((e.target as HTMLSelectElement).value as RoleFilterValue)}
          >
            <option value="ALL">${m.admin_user_list_role_all()}</option>
            <option value="ADMIN">${m.admin_user_list_role_admin()}</option>
            <option value="COMPANY">${m.admin_user_list_role_company()}</option>
            <option value="CANDIDATE">${m.admin_user_list_role_candidate()}</option>
          </select>
        </label>
        <label class="form-control min-w-[180px]">
          <div class="label py-1"><span class="label-text">${m.admin_user_list_application_status_label()}</span></div>
          <select
            class="select select-bordered"
            .value=${applicationStatus}
            @change=${(e: Event) => setApplicationStatus((e.target as HTMLSelectElement).value)}
          >
            <option value="">${m.admin_user_list_application_status_all()}</option>
            <option value=${JobApplicationStatus.PENDING}>${m.application_status_PENDING()}</option>
            <option value=${JobApplicationStatus.ACCEPTED}>${m.application_status_ACCEPTED()}</option>
            <option value=${JobApplicationStatus.BOOKED}>${m.application_status_BOOKED()}</option>
            <option value=${JobApplicationStatus.WAITING}>${m.application_status_WAITING()}</option>
            <option value=${JobApplicationStatus.REJECTED}>${m.application_status_REJECTED()}</option>
            <option value=${JobApplicationStatus.REJECTED_BY_USER}>${m.application_status_REJECTED_BY_USER()}</option>
          </select>
        </label>
        <label class="form-control min-w-[180px]">
          <div class="label py-1"><span class="label-text">${m.admin_user_list_interview_outcome_label()}</span></div>
          <select
            class="select select-bordered"
            .value=${interviewOutcome}
            @change=${(e: Event) => setInterviewOutcome((e.target as HTMLSelectElement).value as InterviewOutcomeFilter)}
          >
            <option value="">${m.admin_user_list_interview_outcome_all()}</option>
            <option value="ATTENDED">${m.admin_user_list_interview_outcome_attended()}</option>
            <option value="NOT_ATTENDED">${m.admin_user_list_interview_outcome_not_attended()}</option>
            <option value="PENDING">${m.admin_user_list_interview_outcome_pending()}</option>
          </select>
        </label>
      </div>

      <div class="flex flex-wrap gap-3 items-end mb-4">
        <label class="form-control min-w-[180px]">
          <div class="label py-1"><span class="label-text">${m.admin_user_list_created_year_label()}</span></div>
          <select
            class="select select-bordered"
            .value=${createdYear === undefined ? '' : String(createdYear)}
            @change=${(e: Event) => {
              const value = (e.target as HTMLSelectElement).value;
              setCreatedYear(value ? Number(value) : undefined);
            }}
          >
            <option value="">${m.admin_user_list_created_year_all()}</option>
            ${(createdYearsQuery.value ?? []).map(year => html`<option value=${String(year)}>${year}</option>`)}
          </select>
        </label>
        <label class="label cursor-pointer justify-start gap-3">
          <input
            type="checkbox"
            class="toggle toggle-primary"
            ?checked=${activeOnly}
            @change=${(e: Event) => setActiveOnly((e.target as HTMLInputElement).checked)}
          />
          <span class="label-text">${m.admin_user_list_active_only_label()}</span>
        </label>
        <button class="btn btn-outline" ?disabled=${mailPending} @click=${() => composeMail()}>
          ${mailPending ? html`<span class="loading loading-spinner loading-sm"></span>` : nothing}
          ${m.admin_user_list_mail_filter_button()}
        </button>
      </div>
      <p class="text-sm opacity-70 mb-4">${m.admin_user_list_active_accounts_hint()}</p>

      ${obs(
        selectionStore.observable.pipe(
          map(ids => {
            if (isEmptySelection(ids)) return nothing;
            return html`
              <div
                class="sticky top-2 z-30 bg-base-100 border border-base-300 rounded-box shadow flex flex-wrap items-center gap-2 px-3 py-2 mb-4"
              >
                <span class="font-semibold text-sm"
                  >${m.admin_user_list_mail_selected({ count: selectionCount(ids) })}</span
                >
                <div class="flex-1"></div>
                <button
                  class="btn btn-sm btn-primary"
                  ?disabled=${mailPending}
                  @click=${() => composeMail(selectionStore.value())}
                >
                  ${m.admin_user_list_mail_compose()}
                </button>
                <button
                  class="btn btn-sm btn-ghost btn-circle"
                  title=${m.admin_user_list_mail_clear_selection()}
                  ?disabled=${mailPending}
                  @click=${() => selectionStore.clear()}
                >
                  <span class="icon-[material-symbols--close]"></span>
                </button>
              </div>
            `;
          }),
        ),
      )}
      ${
        totalQuery.value !== undefined && totalQuery.value !== null
          ? html`<p class="text-sm mb-3" aria-live="polite">${m.admin_user_list_hits({ count: totalQuery.value })}</p>`
          : nothing
      }
      ${DataTable({ options: userDataViewOptions.dataViewOptions })}
    </div>
  `;
});
